/** Thin client over the daemon API; the token lives in localStorage (FRONTEND.md: state comes from the daemon). */
export const base = () => `${location.protocol}//${location.host}`;

export function token(): string {
  const fromUrl = new URLSearchParams(location.search).get('token');
  if (fromUrl) {
    try {
      localStorage.setItem('token', fromUrl);
    } catch {
      /* ignore */
    }
    history.replaceState(null, '', location.pathname + location.hash);
    return fromUrl;
  }
  try {
    return localStorage.getItem('token') ?? '';
  } catch {
    return '';
  }
}

export function setToken(t: string) {
  try {
    localStorage.setItem('token', t);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
  query: Record<string, string | undefined> = {},
): Promise<T> {
  const url = new URL(path, base());
  for (const [k, v] of Object.entries(query)) if (v !== undefined) url.searchParams.set(k, v);
  const res = await fetch(url, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token()}`, ...(init.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`);
  return body;
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | undefined>) => call<T>(path, {}, query),
  post: <T>(path: string, body: unknown = {}) => call<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  events(onEvent: (name: string, payload: unknown) => void): () => void {
    const es = new EventSource(`${base()}/events?token=${encodeURIComponent(token())}`);
    const names = [
      'session.started',
      'session.ended',
      'transcript.segment',
      'note.updated',
      'question.candidate',
      'question.answered',
      'action.proposed',
      'action.approved',
      'schedule.tick',
      'channel.message',
      'log',
    ];
    for (const n of names) es.addEventListener(n, (e) => onEvent(n, JSON.parse((e as MessageEvent).data)));
    return () => es.close();
  },
};

export interface Session {
  id: string;
  scope: { kind: string; org?: string; project?: string };
  kind: string;
  title: string;
  startedAt: number;
  endedAt: number | null;
  segments: number;
  live?: { liveId: string | null; seconds: number } | null;
}
export const scopeId = (s: Session['scope']) =>
  s.kind === 'personal' ? 'personal' : s.kind === 'org' ? `org/${s.org}` : `org/${s.org}/projects/${s.project}`;
